import { jwtDecode } from "jwt-decode";
import { UserManager, WebStorageStateStore } from "oidc-client";

import AppSettings from "../../appsettings";
import { postMessageToApp } from "../../screens/utils/hybridAppHelpers.js";
import { getAccessToken, removeForeignOidcUsers } from "./tokenService";
import { getOidcMetadata } from "./oidcMetadataCache";
import { getAuthProvider } from "./providers";

// oidc-client sets expires_at from the token response's expires_in, which
// describes the access token. When the API bearer is the ID token (Entra: the
// access token is for Graph, randomised to 60-90 min; the ID token is a fixed
// 60), the expiring/expired timers would otherwise fire up to 30 min after
// every API call has started returning 401.
export const clampExpiryToIdToken = (user) => {
  try {
    const { exp } = jwtDecode(user.id_token);
    if (
      typeof exp === "number" &&
      (user.expires_at === undefined || exp < user.expires_at)
    ) {
      user.expires_at = exp;
    }
  } catch {
    // No decodable ID token: keep the access token's expiry.
  }
  return user;
};

// Every sign-in, refresh and hydration path calls storeUser and then arms the
// expiry timers from that same object, so clamping here covers all of them.
class IdTokenBearerUserManager extends UserManager {
  storeUser(user) {
    return super.storeUser(user ? clampExpiryToIdToken(user) : user);
  }
}

export async function getInitializedUserManager() {
  // Cached in localStorage: this runs on every boot, and a cold login boots the
  // app twice. See oidcMetadataCache for the freshness policy.
  const provider = getAuthProvider();
  const config = await getOidcMetadata(provider.wellKnownUrl);

  const clientId = provider.clientId();
  removeForeignOidcUsers(`oidc.user:${config.issuer}:${clientId}`);

  const UserManagerClass =
    provider.apiBearer === "id_token" ? IdTokenBearerUserManager : UserManager;
  const userManager = new UserManagerClass({
    authority: config.issuer,
    client_id: clientId,
    redirect_uri: AppSettings.redirect_uri,
    post_logout_redirect_uri: AppSettings.logout_uri,
    response_type: "code",
    scope: provider.scope,
    userStore: new WebStorageStateStore({ store: globalThis.localStorage }),
    accessTokenExpiringNotificationTime: 60,
    metadata: {
      ...config,
      ...(provider.authorizationEndpoint && {
        authorization_endpoint: provider.authorizationEndpoint,
      }),
    },
    response_mode: provider.responseMode,
    loadUserInfo: false,
  });
  return userManager;
}

// One renewal at a time per UserManager: the expiring timer, the expired timer
// and 401 retries can all ask at once, and each would otherwise spend its own
// token request.
const renewalsInFlight = new WeakMap();
const lastRenewedAt = new WeakMap();

// Long enough to cover a request sent with the old token whose 401 lands just
// after a renewal finishes, plus the QueryClient's auth retry delay.
const RECENT_RENEWAL_MS = 10_000;

export function renewedRecently(userManager) {
  const at = lastRenewedAt.get(userManager);
  return at !== undefined && Date.now() - at < RECENT_RENEWAL_MS;
}

export function signinSilentShared(userManager) {
  let renewal = renewalsInFlight.get(userManager);
  if (!renewal) {
    renewal = Promise.resolve()
      .then(() => userManager.signinSilent())
      .then((user) => {
        lastRenewedAt.set(userManager, Date.now());
        return user;
      })
      .finally(() => renewalsInFlight.delete(userManager));
    renewalsInFlight.set(userManager, renewal);
  }
  return renewal;
}

// Renews an expired session with its refresh token; resolves true on success.
// A renewal already in flight may have been cut off (a laptop sleeping
// mid-request), so failing to join one earns a single fresh attempt.
export async function renewExpiredSession(userManager) {
  const attempts = renewalsInFlight.has(userManager) ? 2 : 1;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const user = await signinSilentShared(userManager);
      if (user?.expired === false) {
        return true;
      }
    } catch (error) {
      console.warn("Expired session could not be renewed.", error);
    }
  }
  return false;
}

export async function renewToken(userManager, lastActivity) {
  try {
    if (!userManager) {
      return;
    }

    if (!getAuthProvider().shouldRenewInBackground(lastActivity)) {
      return;
    }

    await signinSilentShared(userManager);
    const accessToken = getAccessToken();
    postMessageToApp("AccessToken", accessToken);
    // Keep native app's expires_at in sync after a successful silent refresh,
    // so the native IsLoggedIn() check doesn't falsely expire the session.
    const user = await userManager.getUser();
    if (user?.expires_at) {
      postMessageToApp("ExpiresAt", String(user.expires_at * 1000));
    }
  } catch (error) {
    console.error("Error during silent renew on user activity:", error);
    // Let the native app try a refresh_token grant when the browser-based
    // silent renew fails. Without this, the token expires and is revoked,
    // causing cascading "ForgeRock token is not active" 401 errors.
    postMessageToApp("TokenRefreshNeeded", "");
  }
}
